import type { Notification } from '~/shared/types/notification.js'
import { useNotificationsStore } from '~/stores/notifications.js'
/**
 * Get the current user's ID
 * This is wrapped in a separate function to make it easier to mock in tests
 */
export const getCurrentUserId = (): string | null => {
  const user = useCurrentUser()
  return user.value?.uid || null
}

/**
 * Composable providing user-friendly access to notifications
 */
export const useNotifications = () => {
  const notificationsStore = useNotificationsStore()
  const {
    notifications,
    notificationsArray,
    unreadCount,
    loading,
    error,
    initialized,
  } = storeToRefs(notificationsStore)

  // Pagination state
  const page = ref(1)
  const pageSize = ref(10)
  const hasMore = ref(true)
  const loadingMore = ref(false)

  /**
   * Initialize notifications for the current user
   */
  const initializeNotifications = async (): Promise<(() => void) | null> => {
    const userId = getCurrentUserId()
    if (!userId) return null

    // Don't check for initialized state here - always set up a fresh subscription when requested
    // This ensures we properly replace subscriptions when they're cleared

    // Clear existing notification subscriptions to prevent duplicates
    notificationsStore.cleanupSubscriptions()

    console.warn('[Notifications] Setting up new subscription for user', userId)

    // Set up real-time notifications for the current user
    const unsubscribe = notificationsStore.subscribeToNotifications(userId)

    // Return the unsubscribe function
    return unsubscribe
  }

  /**
   * Mark a notification as read
   */
  const markAsRead = async (notificationId: string) => {
    const userId = getCurrentUserId()
    if (!userId) return false

    return await notificationsStore.markAsRead(notificationId, userId)
  }

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    const userId = getCurrentUserId()
    if (!userId) return false

    return await notificationsStore.markAllAsRead(userId)
  }

  /**
   * Delete a notification
   */
  const deleteNotification = async (notificationId: string) => {
    const userId = getCurrentUserId()
    if (!userId) return false

    return await notificationsStore.deleteNotification(notificationId, userId)
  }

  /**
   * Load the initial set of notifications
   */
  const loadNotifications = async () => {
    const userId = getCurrentUserId()
    if (!userId) return []

    page.value = 1
    hasMore.value = true

    // This will rely on the store's subscription mechanism
    // which should already be initialized
    return notificationsArray.value.slice(0, pageSize.value)
  }

  /**
   * Load more notifications for infinite scroll
   */
  const loadMoreNotifications = async () => {
    if (!hasMore.value || loadingMore.value) return []

    loadingMore.value = true

    try {
      const startIndex = page.value * pageSize.value
      const nextBatch = notificationsArray.value.slice(
        startIndex,
        startIndex + pageSize.value,
      )

      // Update pagination state
      page.value++
      hasMore.value = nextBatch.length === pageSize.value

      return nextBatch
    }
    finally {
      loadingMore.value = false
    }
  }

  /**
   * Get notifications by category
   */
  const getNotificationsByCategory = (category: string): Notification[] => {
    return notificationsStore.getNotificationsByCategory(category)
  }

  /**
   * Get a notification by its ID
   */
  const getNotificationById = (id: string): Notification | undefined => {
    return notificationsStore.getNotificationById(id)
  }

  /**
   * Handle notification click
   * This will mark the notification as read and handle navigation if needed
   */
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read first
    if (!notification.read) {
      await markAsRead(notification.id)
    }

    // Handle navigation based on link type
    if (notification.linkType === 'none' || !notification.linkTarget) {
      return // No navigation needed
    }

    // Navigation based on linkType and linkTarget
    const router = useRouter()

    switch (notification.linkType) {
      case 'task':
        router.push(`/`)
        break
      case 'subkrewe':
        // Fix: Use the correct route path and format
        router.push(`/subkrewe?id=${notification.linkTarget}`)
        break
      case 'member':
        router.push(`/members/${notification.linkTarget}`)
        break
      case 'url':
        // For external URLs, open in new tab
        window.open(notification.linkTarget, '_blank')
        break
    }
  }

  return {
    // State
    notifications,
    notificationsArray,
    unreadCount,
    loading,
    error,
    initialized,
    page,
    pageSize,
    hasMore,
    loadingMore,

    // Methods
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadNotifications,
    loadMoreNotifications,
    getNotificationsByCategory,
    getNotificationById,
    handleNotificationClick,
    initializeNotifications,
  }
}

export default useNotifications
